
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function ComparisonTable() {
  const categories = [
    {
      name: 'Gestión de Pacientes',
      features: [
        { name: 'Historiales clínicos digitales', basic: true, pro: true, enterprise: true },
        { name: 'Agenda inteligente', basic: true, pro: true, enterprise: true },
        { name: 'Recordatorios SMS/Email', basic: false, pro: true, enterprise: true },
        { name: 'Comunicación masiva', basic: false, pro: true, enterprise: true },
        { name: 'Portal del paciente', basic: false, pro: false, enterprise: true },
      ],
    },
    {
      name: 'Módulo de Óptica',
      features: [
        { name: 'Catálogo de monturas y lentes', basic: false, pro: true, enterprise: true },
        { name: 'Control de inventario', basic: false, pro: true, enterprise: true },
        { name: 'Promociones automáticas', basic: false, pro: true, enterprise: true },
        { name: 'Integración con proveedores', basic: false, pro: false, enterprise: true },
        { name: 'Código de barras', basic: false, pro: true, enterprise: true },
      ],
    },
    {
      name: 'Diagnóstico y Reportes',
      features: [
        { name: 'Registro de agudeza visual', basic: true, pro: true, enterprise: true },
        { name: 'Presión intraocular con alertas', basic: false, pro: true, enterprise: true },
        { name: 'Imágenes diagnósticas (OCT)', basic: false, pro: true, enterprise: true },
        { name: 'Reportes avanzados', basic: false, pro: true, enterprise: true },
        { name: 'API personalizada', basic: false, pro: false, enterprise: true },
      ],
    },
    {
      name: 'Soporte y Capacitación',
      features: [
        { name: 'Soporte por email', basic: true, pro: true, enterprise: true },
        { name: 'Soporte prioritario 24/7', basic: false, pro: true, enterprise: true },
        { name: 'Capacitación online', basic: true, pro: true, enterprise: true },
        { name: 'Capacitación presencial', basic: false, pro: false, enterprise: true },
        { name: 'Gerente de cuenta dedicado', basic: false, pro: false, enterprise: true },
      ],
    },
  ];

  const renderCheck = (value: boolean) => (
    value ? (
      <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
        <i className="ri-check-line text-emerald-600 text-sm"></i>
      </div>
    ) : (
      <div className="w-6 h-6 flex items-center justify-center mx-auto">
        <span className="text-gray-300">—</span>
      </div>
    )
  );

  return (
    <section className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <AnimatedSection animation="fade-in">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">Comparación</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Compara todas las <span className="font-display italic text-emerald-600">características</span>
            </h2>
          </div>
        </AnimatedSection>

        <AnimatedSection animation="slide-up">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-5 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider w-2/5">Característica</th>
                    <th className="text-center py-5 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Básico</th>
                    <th className="text-center py-5 px-4 text-xs font-bold text-emerald-600 uppercase tracking-wider bg-emerald-50/50">Profesional</th>
                    <th className="text-center py-5 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <>
                      <tr key={category.name} className="bg-gray-50">
                        <td colSpan={4} className="py-3 px-6 text-xs font-bold text-gray-700 uppercase tracking-wider">
                          {category.name}
                        </td>
                      </tr>
                      {category.features.map((feature, fi) => (
                        <tr key={`${category.name}-${fi}`} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                          <td className="py-3.5 px-6 text-sm text-gray-700">{feature.name}</td>
                          <td className="py-3.5 px-4">{renderCheck(feature.basic)}</td>
                          <td className="py-3.5 px-4 bg-emerald-50/30">{renderCheck(feature.pro)}</td>
                          <td className="py-3.5 px-4">{renderCheck(feature.enterprise)}</td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
